// ============================================================================
// Price Formatting Utilities
// ============================================================================

import type { Language } from '@/types'

/**
 * Strip a phone/WhatsApp number down to digits only.
 * WhatsApp's wa.me links require a country-code number with no `+`, spaces,
 * dashes or parentheses — e.g. "+212 612-345-678" -> "212612345678".
 */
export function sanitizePhoneNumber(input: string | undefined | null): string {
  return (input ?? '').replace(/\D/g, '')
}

/**
 * Format a price in MAD (Moroccan Dirham)
 * @param price - The price to format
 * @param showCurrency - Whether to show the currency symbol
 * @returns Formatted price string
 */
export function formatPrice(price: number, showCurrency = true): string {
  // Round to 2 decimal places
  const rounded = Math.round(price * 100) / 100

  // Format with Moroccan locale
  const formatted = rounded.toLocaleString('fr-MA', {
    minimumFractionDigits: rounded % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })

  return showCurrency ? `${formatted} MAD` : formatted
}

/**
 * Format a price range
 */
export function formatPriceRange(min: number, max: number): string {
  if (min === max) {
    return formatPrice(min)
  }
  return `${formatPrice(min, false)} - ${formatPrice(max, false)} MAD`
}

/**
 * Format a "from" price (for items with variants)
 */
export function formatFromPrice(price: number, language: Language = 'fr'): string {
  const formatted = formatPrice(price)
  switch (language) {
    case 'en':
      return `From ${formatted}`
    case 'ar':
      return `ابتداءً من ${formatted}`
    default:
      return `À partir de ${formatted}`
  }
}

/**
 * Calculate the total price of an item with variants and modifiers
 */
export function calculateItemPrice(
  basePrice: number,
  variantPrice?: number,
  variantAdjustment?: number,
  modifiersTotal?: number
): number {
  let total = basePrice

  if (variantPrice !== undefined) {
    total = variantPrice
  } else if (variantAdjustment !== undefined) {
    total += variantAdjustment
  }

  if (modifiersTotal) {
    total += modifiersTotal
  }

  return Math.max(0, total) // Ensure no negative prices
}

/**
 * Calculate cart subtotal
 */
export function calculateCartSubtotal(items: Array<{ price: number; quantity: number }>): number {
  return items.reduce((sum, item) => sum + item.price * item.quantity, 0)
}

/**
 * Format a cart subtotal
 */
export function formatCartSubtotal(subtotal: number): string {
  return `Total: ${formatPrice(subtotal)}`
}

/**
 * Format a discount/savings amount
 */
export function formatSavings(originalPrice: number, discountedPrice: number): string {
  const savings = originalPrice - discountedPrice
  if (savings <= 0) {
    return ''
  }
  return `Économisez ${formatPrice(savings)}`
}

/**
 * Round price to nearest integer (for display)
 */
export function roundPrice(price: number): number {
  return Math.round(price)
}

/**
 * Check if price is an integer (no decimal)
 */
export function isIntegerPrice(price: number): boolean {
  return price % 1 === 0
}
